import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { DENSITIES } from '../calendar/density.js';
import { useAuth } from '../authContext.js';
import { useConfig } from '../configContext.js';

const HELP_LINES = [
  'Rendez-vous :',
  "  Cliquer-glisser sur un créneau pour ouvrir le formulaire d'invitation.",
  '  Le compte sélectionné est automatiquement ajouté comme invité.',
  "  Vous pouvez ajouter d'autres invités via la recherche dans le formulaire.",
  '',
  'Vue Groupe/Jour :',
  '  Clic sur la ligne du nom du groupe = inviter tous les membres.',
  '  Clic sur un membre = inviter uniquement cette personne.',
  '  Shift + clic sur plusieurs membres = sélection multiple.',
  '    Relâchez Shift pour ouvrir le formulaire avec tous les sélectionnés.',
  '  Cliquez sur le triangle pour replier/déplier un groupe.',
  '',
  'Vue Groupe/Semaine :',
  "  Cliquer-glisser sur un créneau = inviter tout ce qui est affiché,",
  '    c\'est-à-dire les groupes cochés moins les membres décochés.',
  '  Décochez des membres dans la barre de gauche pour affiner.',
  '',
  'Navigation :',
  "  Cliquez sur un jour dans l'en-tête pour passer en vue journée.",
  "  Utilisez « Choisir le jour » pour naviguer à une date précise.",
];

const BAR_BUTTON =
  'rounded border border-white/40 bg-white/10 px-2.5 py-1 text-sm text-white hover:bg-white/20';

export default function ButtonsBar({
  setShowAddAccountModal,
  setShowGroupManagerModal,
  setShowDatePicker,
  updatePosition,
  ctrlActive,
  pendingCount,
  density,
  setDensity,
}) {
  const { logout } = useAuth();
  const { appTitle } = useConfig();
  const [menuOpen, setMenuOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const menuRef = useRef(null);

  // Close the burger on a click outside it, and on Escape.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setMenuOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const runAndClose = (action) => {
    setMenuOpen(false);
    action();
  };

  return (
    <header className="flex items-center justify-between gap-2 border-b border-gray-200 bg-brand-500 px-3 py-2 shadow-sm">
      {/* Titre + burger (petit écran, là où la barre latérale est masquée) */}
      <div className="flex items-center gap-2" ref={menuRef}>
        <h1 className="whitespace-nowrap text-lg font-bold text-white">{appTitle}</h1>
        <div className="relative md:hidden">
          <button
            type="button"
            aria-label="Menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
            className="flex h-9 w-9 items-center justify-center rounded text-xl text-white hover:bg-white/20"
          >
            &#9776;
          </button>
          {menuOpen && (
            <ul className="absolute left-0 top-10 z-30 w-56 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
              <MenuItem onClick={() => runAndClose(() => setShowAddAccountModal(true))}>
                Ajouter un compte
              </MenuItem>
              <MenuItem onClick={() => runAndClose(() => setShowGroupManagerModal(true))}>
                Groupes
              </MenuItem>
              <MenuItem
                onClick={(event) =>
                  runAndClose(() => {
                    setShowDatePicker((prev) => !prev);
                    updatePosition(event);
                  })
                }
              >
                Choisir le jour
              </MenuItem>
            </ul>
          )}
        </div>
      </div>

      {/* Actions de vue (grand écran) */}
      <div className="hidden items-center gap-2 md:flex">
        <button
          type="button"
          className={BAR_BUTTON}
          onClick={(event) => {
            setShowDatePicker((prev) => !prev);
            updatePosition(event);
          }}
        >
          Choisir le jour
        </button>
        {/* Hauteur de ligne : quatre calendriers et vingt calendriers ne veulent
            pas la même grille, et le choix est mémorisé. */}
        <select
          value={density}
          onChange={(event) => setDensity(event.target.value)}
          aria-label="Hauteur des lignes"
          title="Hauteur des lignes"
          className={`${BAR_BUTTON} outline-none`}
        >
          {Object.entries(DENSITIES).map(([key, { label }]) => (
            <option key={key} value={key} className="text-gray-900">
              {label}
            </option>
          ))}
        </select>
      </div>

      {/* Compteur de sélection + aide + déconnexion */}
      <div className="flex shrink-0 items-center gap-2">
        {ctrlActive && pendingCount > 0 && (
          <span role="status" aria-live="polite" className="whitespace-nowrap text-sm font-bold text-white">
            {pendingCount} sélectionné{pendingCount > 1 ? 's' : ''}
          </span>
        )}
        <div
          className="relative"
          onMouseEnter={() => setHelpOpen(true)}
          onMouseLeave={() => setHelpOpen(false)}
        >
          <button
            type="button"
            aria-label="Aide"
            aria-expanded={helpOpen}
            onClick={() => setHelpOpen((open) => !open)}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-sm font-bold text-white hover:bg-white/30"
          >
            ?
          </button>
          {helpOpen && (
            <div
              role="tooltip"
              className="absolute right-0 top-10 z-30 w-[28rem] max-w-[90vw] whitespace-pre-wrap rounded-md border border-gray-200 bg-white px-3 py-2 text-[13px] leading-relaxed text-gray-700 shadow-lg"
            >
              {HELP_LINES.join('\n')}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={logout}
          className="rounded bg-red-50 px-2.5 py-1 text-sm font-medium text-red-700 hover:bg-red-100"
        >
          Se déconnecter
        </button>
      </div>
    </header>
  );
}

ButtonsBar.propTypes = {
  setShowAddAccountModal: PropTypes.func,
  setShowGroupManagerModal: PropTypes.func,
  setShowDatePicker: PropTypes.func,
  updatePosition: PropTypes.func,
  ctrlActive: PropTypes.bool,
  pendingCount: PropTypes.number,
  density: PropTypes.string,
  setDensity: PropTypes.func,
};

function MenuItem({ onClick, children }) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
      >
        {children}
      </button>
    </li>
  );
}

MenuItem.propTypes = {
  onClick: PropTypes.func.isRequired,
  children: PropTypes.node,
};
